'use client';

import { useOrganization } from '@clerk/nextjs';
import useHardSignOut from '@/hooks/useHardSignOut';
import { BuildingOfficeIcon, ArrowLeftOnRectangleIcon } from '@heroicons/react/24/outline';
import { useTranslations } from 'next-intl';
import OnboardingOrgSwitcher from '@/components/onboarding/OnboardingOrgSwitcher';

interface InactiveAccountScreenProps {
  orgName?: string | null;
  /**
   * When true, render copy for "we couldn't find this organization in our
   * system" instead of "this organization is inactive". Same UI shape -- the
   * user still needs to switch orgs or contact support.
   */
  variant?: 'inactive' | 'missing';
}

export default function InactiveAccountScreen({ orgName, variant = 'inactive' }: InactiveAccountScreenProps) {
  const t = useTranslations('shared.inactiveAccountScreen');
  const { organization: clerkOrg } = useOrganization();
  const handleSignOut = useHardSignOut();
  const heading = variant === 'missing'
    ? t('headingMissing')
    : t('headingInactive');
  const orgLabel = orgName || clerkOrg?.name || 'this organization';
  const body = variant === 'missing'
    ? t.rich('bodyMissing', {
        orgLabel,
        strong: (chunks) => <strong style={{ color: 'var(--color-text-primary)' }}>{chunks}</strong>,
      })
    : t.rich('bodyInactive', {
        orgLabel,
        strong: (chunks) => <strong style={{ color: 'var(--color-text-primary)' }}>{chunks}</strong>,
      });
  return (
    <div
      className="flex min-h-screen items-center justify-center px-6 py-12"
      style={{ background: 'var(--color-background)' }}
    >
      <div
        className="w-full max-w-md rounded-2xl border p-8 shadow-xl"
        style={{
          background: 'var(--color-surface)',
          borderColor: 'var(--color-border)',
        }}
      >
        <div className="mb-6 flex justify-center">
          <div
            className="flex h-14 w-14 items-center justify-center rounded-full"
            style={{ background: 'var(--color-surface-hover)' }}
          >
            <BuildingOfficeIcon
              className="h-7 w-7"
              style={{ color: 'var(--color-text-muted)' }}
            />
          </div>
        </div>

        <h1
          className="mb-2 text-center text-xl font-semibold"
          style={{ color: 'var(--color-text-primary)' }}
        >
          {heading}
        </h1>
        <p
          className="mb-6 text-center text-sm leading-relaxed"
          style={{ color: 'var(--color-text-muted)' }}
        >
          {body}
        </p>

        <div className="mb-4 flex justify-center">
          <OnboardingOrgSwitcher alwaysShow />
        </div>

        <div className="my-4 h-px" style={{ background: 'var(--color-border)' }} />

        <button
          type="button"
          onClick={handleSignOut}
          className="flex w-full items-center justify-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors hover:bg-[var(--color-surface-hover)]"
          style={{ color: 'var(--color-text-primary)' }}
        >
          <ArrowLeftOnRectangleIcon className="h-4 w-4" />
          {t('signOut')}
        </button>
      </div>
    </div>
  );
}
